import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnChanges } from '@angular/core';

import { BetType } from '@bpos/common';
import { BestOddsGuaranteedState } from '@bpos/v1/my-bets';
import { BetslipConfig, EpcotConfig, LiveNavigationConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { Odds, OddsFormatterService } from '@frontend/sports/odds/feature';
import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { LiveIndicatorType } from '@frontend/sports/types/components/features';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { NgxFloatUiPlacements } from 'ngx-float-ui';

import { MarketSubTypeLogoStrategy } from '../../../../../lib/marketsubtype-logo/marketsubtype-logo-display.strategy';
import { EachWayMarketHelperService } from '../../../../../lib/option-group/each-way-market-helper.service';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from '../../../../core/picks/betslip-group-pick';
import { BetslipV1Pick } from '../../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2Pick } from '../../../../core/picks/betslip-v2-pick';
import { PriceType } from '../../../../core/picks/pick-models';
import { BetslipV2GolfWinParticipantPick } from '../../../../core/picks/sport-specific/betslip-v2-golf-picks';
import {
    BetslipV2HorseRaceOptionMarketPick,
    BetslipV2OptionMarketXCastRacePick,
} from '../../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { getOptionNameFromBetslipPick } from '../../../../core/utils';
import { ISucceededSlipPickResult } from '../../../betplacement/models';
import { ITeaserBetPickState } from '../../../teaser-bet/state';
import { TokenPayoutSummary } from '../../models/betslip-result-summary.model';
import { IResultSucceededSlipSummaryViewModel } from '../../models/result-succeeded-slip.model';

export enum SucceededPickType {
    Option,
    Participant,
    BetBuilder,
}

@Component({
    selector: 'bs-digital-result-succeeded-pick',
    templateUrl: './result-succeeded-pick.component.html',
    styleUrls: ['./result-succeeded-pick.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ResultSucceededPickComponent implements OnChanges {
    @Input() pick: ISucceededSlipPickResult;
    @Input() boostedOdds: Odds | undefined;
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() summary: IResultSucceededSlipSummaryViewModel;
    @Input() tokenPayoutSummary?: TokenPayoutSummary;
    @Input() isLinear = false;
    @Input() isSummaryVisible = true;
    @Input() teaserPicks?: { [pickId: string]: ITeaserBetPickState };

    headerTitle = '';
    headerSubTitle = '';
    isEpcotEnabled = this.epcotConfig.isEnabled;
    marketLogoShow = false;

    @HostBinding('class') className = 'betslip-result-succeeded-pick';

    @HostBinding('class.betslip-digital-pick-bet-builder') get isBetBuilder(): boolean {
        return this.pickType === SucceededPickType.BetBuilder;
    }

    @HostBinding('class.betslip-result-succeeded-priceboosted-pick') get isPriceBoost(): boolean {
        return this.pick.clientPick?.boostedPriceDetails?.isPriceBoosted!;
    }

    @HostBinding('class.betslip-result-succeeded-boosted-odds') get hasBoostedOdds(): boolean {
        return !!this.boostedOdds;
    }

    betSlipType = BetslipType;
    pickTypes = SucceededPickType;
    pickType: SucceededPickType;
    messages: {
        live: string;
        betBuilderMarketName: string;
        eachWay: string;
        startingPrice: string;
    };
    isBadgeIndicator = this.liveNavConfig.liveIndicatorType === LiveIndicatorType.Badge;
    readonly isSgpBadgeInTitleEnabled = this.betslipConfig.isSgpBadgeInTitleEnabled;

    constructor(
        private oddsFormatter: OddsFormatterService,
        private eachWayMarketHelper: EachWayMarketHelperService,
        public sitecore: Sitecore,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private epcotConfig: EpcotConfig,
        private interpolationPipe: StringInterpolationPipe,
        private betslipConfig: BetslipConfig,
        private marketSubTypeLogoStrategy: MarketSubTypeLogoStrategy,
        private marketGroupingConfig: MarketGroupingConfig,
        private liveNavConfig: LiveNavigationConfig,
    ) {
        const messages = messageProvider.getMessages();
        this.messages = {
            live: messages.common.Live,
            betBuilderMarketName: messages.betBuilder.marketName,
            eachWay: messages.common.EachWay,
            startingPrice: messages.common.StartingPrice,
        };
    }

    ngOnChanges(): void {
        if (this.pick.clientPick) {
            if (this.pick.clientPick instanceof BetslipV2OptionMarketPick) {
                this.marketLogoShow = this.pick.clientPick.option.isDraw === false && this.marketSubTypeLogoStrategy.shouldShowLogo;
            }
            if (this.pick.clientPick instanceof BetslipV1Pick) {
                this.marketLogoShow = this.marketGroupingConfig.templateIdsForSecondChanceBadge?.includes(
                    Number(this.pick.clientPick?.market.templateId),
                );
            }
            if (BetslipBetBuilderPick.isPick(this.pick.clientPick) || BaseBetslipGroupPick.isPick(this.pick.clientPick)) {
                this.pickType = SucceededPickType.BetBuilder;
                if (this.isEpcotBBMultisEnabled) {
                    this.headerTitle = this.messages.betBuilderMarketName;
                    this.headerSubTitle = this.interpolationPipe.transform(this.sitecore.betslip.messages.BetbuilderComboHeaderSubTitle, {
                        legsCount: this.getLegs().length,
                    });
                }
            } else if (BetslipV2ParticipantPick.isPick(this.pick.clientPick)) {
                this.pickType = SucceededPickType.Participant;
            } else {
                this.pickType = SucceededPickType.Option;
            }
        } else {
            this.pickType = SucceededPickType.Option;
        }
    }

    getLegs(): string[] {
        if (this.pick.clientPick) {
            if (BetslipBetBuilderPick.isPick(this.pick.clientPick)) {
                return this.pick.clientPick.getLegs();
            }
            if (BaseBetslipGroupPick.isPick(this.pick.clientPick)) {
                return this.pick.clientPick.getLegs().map(getOptionNameFromBetslipPick);
            }
        }

        return [];
    }

    get isEpcotBBMultisEnabled(): boolean {
        return this.betslipConfig.allowBetbuilderCombos && this.isEpcotEnabled && this.summary.betType === this.betSlipType.Combo;
    }

    get odds(): string {
        return this.pick.clientPick?.priceType === PriceType.StartingPrice
            ? this.messages.startingPrice
            : this.oddsFormatter.format(this.pick.odds, { displayFormat: this.oddsDisplayFormat });
    }

    get optionName(): string {
        if (this.pick.isTeaserBet && this.pick.clientPick && this.teaserPicks) {
            const selectedPick = this.teaserPicks[this.pick.clientPick.id.toString()];

            if (selectedPick) {
                return this.pick.optionNames
                    .map(() => `${selectedPick.selectedOption!.name} > ${selectedPick.newPoints > 0 ? '+' : ''}${selectedPick.newPoints}`)
                    .join(', ');
            }
        }

        return this.pick.optionNames.join(', ');
    }

    get isLive(): boolean {
        return !!this.pick.clientPick?.isLive;
    }

    get priceBoostOdds(): string {
        return this.oddsFormatter.format(this.pick.clientPick?.boostedPriceDetails?.boostedPrice?.nativeOdds, {
            displayFormat: this.oddsDisplayFormat,
        });
    }

    get wasPriceOdds(): string {
        return this.oddsFormatter.format(this.pick.clientPick?.acceptedPrice!.price?.nativeOdds, { displayFormat: this.oddsDisplayFormat });
    }

    get isEachWay(): boolean {
        const betDetails = this.pick.participantBetDetails ?? this.pick.optionBetDetails;

        return betDetails?.betType === BetType.EachWay;
    }

    get isGolfPick(): boolean {
        return this.pick.clientPick instanceof BetslipV2GolfWinParticipantPick;
    }

    get isHorsesPick(): boolean {
        return this.pick.clientPick instanceof BetslipV2HorseRaceOptionMarketPick;
    }

    get isHorsesPickAnyCast(): boolean {
        return this.pick.clientPick instanceof BetslipV2OptionMarketXCastRacePick;
    }

    get participantMarketName(): string {
        return this.isGolfPick ? this.pick.marketName : (this.eachWayText ?? this.pick.marketName);
    }

    get eachWayText(): string | undefined {
        const placeTerm = (this.pick.participantBetDetails ?? this.pick.optionBetDetails)?.placeTerm;
        if (placeTerm && this.isEachWay) {
            return `${this.messages.eachWay} ${this.eachWayMarketHelper.calculateEachWayTerms(placeTerm)}`;
        }

        return;
    }

    get isBestOddsGuaranteedEligible() {
        return this.pick.participantBetDetails?.bestOddsGuaranteedState === BestOddsGuaranteedState.Eligible;
    }

    get isOptionBetBOGEligible() {
        return this.pick.optionBetDetails?.bestOddsGuaranteedState === BestOddsGuaranteedState.Eligible;
    }

    get optionMarketName(): string {
        if (
            this.pick.optionBetDetails?.betTypeName !== undefined &&
            ((this.isHorsesPick && this.pick.clientPick?.market?.marketType?.toString() === RacingOptionMarketTypes.WinEw) ||
                this.isHorsesPickAnyCast)
        ) {
            return this.pick.optionBetDetails?.betType === BetType.EachWay ? this.eachWayText! : this.pick.optionBetDetails?.betTypeName;
        }

        return this.isPriceBoost ? `${this.pick.marketName} - ${this.sitecore.event.PriceBoostMarket}` : (this.eachWayText ?? this.pick.marketName);
    }

    get betCount(): number {
        return this.pick.clientPick?.betCount() ?? 1;
    }

    get isMultiplierVisible(): boolean {
        return this.isEachWay || this.betCount > 1;
    }

    get multiplierValue(): string {
        const multiplier = this.isEachWay ? 2 : this.betCount;

        return `${multiplier}x`;
    }

    get stakeValue(): number | undefined {
        return this.isEachWay ? this.summary.stakePerBet : this.summary.stake;
    }

    get isTeaserBet(): boolean {
        return this.pick.isTeaserBet!;
    }

    get isStartingPrice(): boolean {
        return this.pick.clientPick instanceof BetslipV2HorseRaceOptionMarketPick && this.pick.clientPick?.priceType === PriceType.StartingPrice;
    }

    get isVirtual(): boolean {
        return this.pick.clientPick instanceof BetslipV2Pick && this.pick.clientPick.fixture.virtual;
    }

    protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
}
